import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentDeviceFeeChargeStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalFeeDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { formatDeviceDate, todayInIsrael } from '@/helpers/paymentDevices';
import { FeeAudience, feeChargeStatusKey, feeChargeTone, formatFeeAmount, formatFeeMonth, nextChargeDate } from '@/helpers/paymentDeviceFees';
import { Pill } from './Pill';

interface RentalFeeSummaryProps {
  fee: PaymentDeviceRentalFeeDtoIn;
  /** One line for the office's blades instead of the coordinator's three boxes. */
  compact?: boolean;
  /**
   * The coordinator sees «שולם» / «ישולם ב-…»: from the fund's side, what the monthly run recorded in
   * APT is what it paid. The office keeps the APT wording, which says where the money moved.
   */
  audience?: FeeAudience;
}

/**
 * What a rental has cost (HAT-1434, part 3): the fee so far, how much of it the monthly run already
 * recorded in APT, and what the next 1st of the month will record - with the months behind it on
 * request. Every number comes from the server; nothing is computed here.
 */
const RentalFeeSummary = ({ fee, compact = false, audience = 'office' }: RentalFeeSummaryProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const isSimulated = fee.charges.some((charge) => charge.status === PaymentDeviceFeeChargeStatus.Simulated);
  const forCoordinator = audience === 'coordinator';
  const chargedLabel = forCoordinator
    ? (isSimulated ? t('paymentDevices.fees.paidSimulated') : t('paymentDevices.fees.paid'))
    : (isSimulated ? t('paymentDevices.fees.chargedSimulated') : t('paymentDevices.fees.charged'));
  const nextDate = formatDeviceDate(nextChargeDate(todayInIsrael()));
  const pendingLabel = forCoordinator
    ? t('paymentDevices.fees.payOn', { date: nextDate })
    : t('paymentDevices.fees.pendingOn', { date: nextDate });

  const breakdown = fee.charges.length > 0 && (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((open) => !open); }}
        aria-expanded={isOpen}
        className="self-start text-[13px] font-semibold text-(--font-tertiary) underline-offset-2 hover:underline cursor-pointer bg-transparent border-0 p-0"
      >
        {isOpen ? t('paymentDevices.fees.hideMonths') : t('paymentDevices.fees.showMonths', { count: fee.charges.length })}
      </button>
      {isOpen && (
        <ul className="list-none m-0 p-0 flex flex-col rounded-lg border border-(--border-divider) overflow-hidden">
          {fee.charges.map((charge) => (
            <li key={charge.chargeMonth} className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-center px-3.5 py-2.5 text-sm [&:not(:first-child)]:border-t border-(--border-divider)">
              <span className="font-medium">{formatFeeMonth(charge.chargeMonth)}</span>
              <span className="text-(--font-body-2)">{t('paymentDevices.fees.businessDays', { count: charge.businessDays })}</span>
              <span className="font-semibold">{formatFeeAmount(charge.amount)}</span>
              <Pill tone={feeChargeTone(charge.status)} small>{t(feeChargeStatusKey(charge.status, audience))}</Pill>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  if (compact) {
    return (
      <div className="flex flex-col gap-1.5 text-[13px]">
        <span>
          <span className="font-semibold">{t('paymentDevices.fees.accrued')}: {formatFeeAmount(fee.accruedAmount)}</span>
          <span className="text-(--font-body-2)"> ({t('paymentDevices.fees.businessDays', { count: fee.accruedDays })})</span>
          <span className="text-(--font-body-2)"> · {chargedLabel}: {formatFeeAmount(fee.chargedAmount)}</span>
          {fee.pendingAmount > 0 && <span className="text-(--font-body-2)"> · {pendingLabel}: {formatFeeAmount(fee.pendingAmount)}</span>}
        </span>
        {breakdown}
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3 pt-3.5 border-t border-(--border-divider)" aria-label={t('paymentDevices.fees.title')}>
      <span className="text-[13px] font-semibold text-(--font-body-2)">{t('paymentDevices.fees.title')}</span>
      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col gap-0.5 rounded-lg bg-(--surface-primary-lowest) px-3 py-2.5">
          <span className="text-xs text-(--font-body-3)">{t('paymentDevices.fees.accrued')}</span>
          <span className="text-lg font-bold text-(--font-primary)">{formatFeeAmount(fee.accruedAmount)}</span>
          <span className="text-xs text-(--font-body-2)">{t('paymentDevices.fees.businessDays', { count: fee.accruedDays })}</span>
        </div>
        <div className="flex flex-col gap-0.5 rounded-lg bg-(--surface-grey-lowest) px-3 py-2.5">
          <span className="text-xs text-(--font-body-3)">{chargedLabel}</span>
          <span className="text-lg font-bold">{formatFeeAmount(fee.chargedAmount)}</span>
        </div>
        <div className="flex flex-col gap-0.5 rounded-lg bg-(--surface-grey-lowest) px-3 py-2.5">
          <span className="text-xs text-(--font-body-3)">{pendingLabel}</span>
          <span className="text-lg font-bold">{formatFeeAmount(fee.pendingAmount)}</span>
        </div>
      </div>
      {breakdown}
    </section>
  );
};

export default RentalFeeSummary;
